import { call, put, takeLatest, select } from 'redux-saga/effects';
import apiRequest from '@libs/apiRequest';
import { setSnack, setLoading } from 'utilities/redux.actions';
import * as T from './redux.actionTypes';
import * as actions from './redux.actions';
import * as selectors from './redux.selectors';
import { API_ENDPOINTS, getErrorMessage } from './common';

// ========== List ==========
function* fetchClientsSaga({ payload = {} }) {
    try {
        const filters = yield select(selectors.filtersSelector);
        const pagination = yield select(selectors.paginationSelector);
        const sort = yield select(selectors.sortSelector);

        const params = {
            limit: payload.limit || pagination.limit,
            offset: payload.offset || pagination.offset,
            'sort-by': payload.sortBy || sort.sortBy,
            'sort-descending': payload.sortDescending !== undefined ? payload.sortDescending : sort.sortDescending
        };
        const search = payload.search !== undefined ? payload.search : filters.search;
        const type = payload.type !== undefined ? payload.type : filters.type;
        const isActive = payload.isActive !== undefined ? payload.isActive : filters.isActive;
        const employeeEmail = payload.employeeEmail !== undefined ? payload.employeeEmail : filters.employeeEmail;
        const teamId = payload.teamId !== undefined ? payload.teamId : filters.teamId;
        if (search) params.search = search;
        if (type) params.type = type;
        if (isActive !== '' && isActive !== undefined && isActive !== null) params.isActive = isActive;
        if (employeeEmail) params.employeeEmail = employeeEmail;
        if (teamId) params.teamId = teamId;

        yield put(setLoading(true));
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_ALL,
            method: 'GET',
            params
        });
        yield put(actions.fetchClientsSuccess(response.data));
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to fetch clients');
        yield put(actions.fetchClientsFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    } finally {
        yield put(setLoading(false));
    }
}

// ========== Detail ==========
function* fetchClientDetailSaga({ payload }) {
    try {
        yield put(setLoading(true));
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.GET_DETAIL}/${payload.id}/detail`,
            method: 'GET'
        });
        yield put(actions.fetchClientDetailSuccess(response.data));
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to load client details');
        yield put(actions.fetchClientDetailFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    } finally {
        yield put(setLoading(false));
    }
}

// ========== Create / Update / Delete ==========
function* createClientSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.CREATE,
            method: 'POST',
            data: JSON.stringify(payload)
        });
        yield put(actions.createClientSuccess(response.data));
        yield put(actions.closeClientDialog());
        yield put(setSnack({ message: 'Client created successfully', type: 'success' }));
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to create client');
        yield put(actions.createClientFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    }
}

function* updateClientSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE}/${payload.id}`,
            method: 'PUT',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.updateClientSuccess(response.data));
        yield put(actions.closeClientDialog());
        yield put(setSnack({ message: 'Client updated successfully', type: 'success' }));
        yield put(actions.fetchClients());
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to update client');
        yield put(actions.updateClientFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    }
}

function* deleteClientSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `${API_ENDPOINTS.DELETE}/${payload.id}`,
            method: 'DELETE'
        });
        yield put(actions.deleteClientSuccess(payload.id));
        yield put(actions.closeDeleteDialog());
        yield put(setSnack({ message: 'Client deleted successfully', type: 'success' }));
        yield put(actions.fetchClients());
    } catch (error) {
        const msg = getErrorMessage(error, 'Failed to delete client');
        yield put(actions.deleteClientFailure(msg));
        yield put(setSnack({ message: msg, type: 'error' }));
    }
}

// ========== Websites ==========
function* addWebsiteSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/websites`,
            method: 'POST',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.addClientWebsiteSuccess(response.data));
        yield put(setSnack({ message: 'Website added', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to add website'), type: 'error' }));
    }
}
function* updateWebsiteSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/websites/${payload.websiteId}`,
            method: 'PUT',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.updateClientWebsiteSuccess(response.data));
        yield put(setSnack({ message: 'Website updated', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to update website'), type: 'error' }));
    }
}
function* deleteWebsiteSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/client/${payload.clientId}/websites/${payload.websiteId}`,
            method: 'DELETE'
        });
        yield put(actions.deleteClientWebsiteSuccess(payload.websiteId));
        yield put(setSnack({ message: 'Website removed', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to remove website'), type: 'error' }));
    }
}

// ========== Work Periods ==========
function* addPeriodSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/periods`,
            method: 'POST',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.addClientPeriodSuccess(response.data));
        yield put(setSnack({ message: 'Work period added', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to add work period'), type: 'error' }));
    }
}
function* updatePeriodSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/periods/${payload.periodId}`,
            method: 'PUT',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.updateClientPeriodSuccess(response.data));
        yield put(setSnack({ message: 'Work period updated', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to update period'), type: 'error' }));
    }
}
function* deletePeriodSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/client/${payload.clientId}/periods/${payload.periodId}`,
            method: 'DELETE'
        });
        yield put(actions.deleteClientPeriodSuccess(payload.periodId));
        yield put(setSnack({ message: 'Work period removed', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to remove period'), type: 'error' }));
    }
}

// ========== Git Repos ==========
function* addGitRepoSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/git-repos`,
            method: 'POST',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.addClientGitRepoSuccess(response.data));
        yield put(setSnack({ message: 'Git repo added', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to add git repo'), type: 'error' }));
    }
}
function* updateGitRepoSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/git-repos/${payload.repoId}`,
            method: 'PUT',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.updateClientGitRepoSuccess(response.data));
        yield put(setSnack({ message: 'Git repo updated', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to update git repo'), type: 'error' }));
    }
}
function* deleteGitRepoSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/client/${payload.clientId}/git-repos/${payload.repoId}`,
            method: 'DELETE'
        });
        yield put(actions.deleteClientGitRepoSuccess(payload.repoId));
        yield put(setSnack({ message: 'Git repo removed', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to remove git repo'), type: 'error' }));
    }
}

// ========== Stack ==========
function* addStackSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/stacks`,
            method: 'POST',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.addClientStackSuccess(response.data));
        yield put(setSnack({ message: 'Stack item added', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to add stack item'), type: 'error' }));
    }
}
function* updateStackSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/stacks/${payload.stackId}`,
            method: 'PUT',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.updateClientStackSuccess(response.data));
        yield put(setSnack({ message: 'Stack item updated', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to update stack item'), type: 'error' }));
    }
}
function* deleteStackSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/client/${payload.clientId}/stacks/${payload.stackId}`,
            method: 'DELETE'
        });
        yield put(actions.deleteClientStackSuccess(payload.stackId));
        yield put(setSnack({ message: 'Stack item removed', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to remove stack item'), type: 'error' }));
    }
}

// ========== Employees ==========
function* assignEmployeeSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/employees`,
            method: 'POST',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.assignEmployeeSuccess(response.data));
        yield put(setSnack({ message: 'Employee assigned', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to assign employee'), type: 'error' }));
    }
}
function* updateEmployeeAssignmentSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/employees/${encodeURIComponent(payload.email)}`,
            method: 'PATCH',
            data: JSON.stringify(payload.data)
        });
        yield put(actions.updateEmployeeAssignmentSuccess(response.data));
        yield put(setSnack({ message: 'Employee updated', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to update employee'), type: 'error' }));
    }
}
function* unassignEmployeeSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/client/${payload.clientId}/employees/${encodeURIComponent(payload.email)}`,
            method: 'DELETE'
        });
        yield put(actions.unassignEmployeeSuccess(payload.email));
        yield put(setSnack({ message: 'Employee unassigned', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to unassign employee'), type: 'error' }));
    }
}

// ========== Teams ==========
function* assignTeamSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `/client/${payload.clientId}/teams`,
            method: 'POST',
            data: JSON.stringify({ teamId: payload.teamId })
        });
        yield put(actions.assignTeamSuccess(response.data));
        yield put(setSnack({ message: 'Team assigned', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to assign team'), type: 'error' }));
    }
}
function* unassignTeamSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `/client/${payload.clientId}/teams/${payload.teamId}`,
            method: 'DELETE'
        });
        yield put(actions.unassignTeamSuccess(payload.teamId));
        yield put(setSnack({ message: 'Team removed', type: 'success' }));
    } catch (error) {
        yield put(setSnack({ message: getErrorMessage(error, 'Failed to remove team'), type: 'error' }));
    }
}
function* fetchTeamOptionsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_TEAMS,
            method: 'GET',
            params: { limit: 200, offset: 1, isActive: true }
        });
        const data = response?.data?.data || response?.data || [];
        yield put(actions.fetchTeamOptionsSuccess(data));
    } catch (error) {
        yield put(actions.fetchTeamOptionsFailure(getErrorMessage(error, 'Failed to load teams')));
    }
}

function* fetchEmployeeOptionsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: '/employee/get/all/preview-role-based',
            method: 'GET',
            params: { limit: 500 }
        });
        const data = response?.data?.data || response?.data || [];
        yield put(actions.fetchEmployeeOptionsSuccess(Array.isArray(data) ? data : []));
    } catch (error) {
        yield put(actions.fetchEmployeeOptionsFailure(getErrorMessage(error, 'Failed to load employees')));
        yield put(actions.fetchEmployeeOptionsSuccess([]));
    }
}

// Refetch list on filter/pagination/sort change
function* refetchOnChange() {
    yield put(actions.fetchClients());
}

export default function* clientManagementSaga() {
    yield takeLatest(T.FETCH_CLIENTS, fetchClientsSaga);
    yield takeLatest(T.FETCH_CLIENT_DETAIL, fetchClientDetailSaga);
    yield takeLatest(T.CREATE_CLIENT, createClientSaga);
    yield takeLatest(T.UPDATE_CLIENT, updateClientSaga);
    yield takeLatest(T.DELETE_CLIENT, deleteClientSaga);

    yield takeLatest(T.ADD_CLIENT_WEBSITE, addWebsiteSaga);
    yield takeLatest(T.UPDATE_CLIENT_WEBSITE, updateWebsiteSaga);
    yield takeLatest(T.DELETE_CLIENT_WEBSITE, deleteWebsiteSaga);

    yield takeLatest(T.ADD_CLIENT_PERIOD, addPeriodSaga);
    yield takeLatest(T.UPDATE_CLIENT_PERIOD, updatePeriodSaga);
    yield takeLatest(T.DELETE_CLIENT_PERIOD, deletePeriodSaga);

    yield takeLatest(T.ADD_CLIENT_GIT_REPO, addGitRepoSaga);
    yield takeLatest(T.UPDATE_CLIENT_GIT_REPO, updateGitRepoSaga);
    yield takeLatest(T.DELETE_CLIENT_GIT_REPO, deleteGitRepoSaga);

    yield takeLatest(T.ADD_CLIENT_STACK, addStackSaga);
    yield takeLatest(T.UPDATE_CLIENT_STACK, updateStackSaga);
    yield takeLatest(T.DELETE_CLIENT_STACK, deleteStackSaga);

    yield takeLatest(T.ASSIGN_EMPLOYEE, assignEmployeeSaga);
    yield takeLatest(T.UPDATE_EMPLOYEE_ASSIGNMENT, updateEmployeeAssignmentSaga);
    yield takeLatest(T.UNASSIGN_EMPLOYEE, unassignEmployeeSaga);

    yield takeLatest(T.ASSIGN_TEAM, assignTeamSaga);
    yield takeLatest(T.UNASSIGN_TEAM, unassignTeamSaga);
    yield takeLatest(T.FETCH_TEAM_OPTIONS, fetchTeamOptionsSaga);
    yield takeLatest(T.FETCH_EMPLOYEE_OPTIONS, fetchEmployeeOptionsSaga);

    yield takeLatest([T.SET_FILTERS, T.SET_PAGINATION, T.SET_SORT], refetchOnChange);
}
